<template>
    <div class="container">
        <nav class="nav">
            <!-- <div class="nav-header">
                <div class="nav-title">CoExCell</div>
                <div class="nav-subtitle">Database Documentation</div>
            </div> -->
            <ul class="nav-menu">
                <li class="nav-item" v-for="(item, index) in contentList" :key="item.title">
                    <a :href="`#${generateId(item.title)}`" :class="['nav-link', { active: index === 0 }]">{{ item.title }}</a>
                </li>
            </ul>
        </nav>

        <main class="main-content">
            <!-- <header class="page-header">
                <h1 class="page-title">CoExCell</h1>
                <p class="page-subtitle">A comprehensive resource that constructs cell type specific and spatially resolved gene coexpression networks by integrating single-cell and spatial transcriptomics</p>
            </header> -->

            <section v-for="item in contentList" :key="item.title" :id="generateId(item.title)" class="section">
                <h2 class="section-title">{{ item.title }}</h2>
                <div class="section-content">
                    <template v-if="item.content && item.content.length > 0">
                        <p v-for="(pText, index) in item.content" :key="index">{{ pText }}</p>
                    </template>

                    <template v-if="item.images && item.images.length > 0">
                        <img
                            v-for="(imgSrc, index) in item.images"
                            :key="index"
                            :src="imgSrc"
                            :class="{ 'analysis-pipeline-image': item.title === 'Analysis Pipeline' }"
                            :alt="item.title === 'Analysis Pipeline' ? 'Single-cell and spatial data analysis pipeline' : ''"
                        >
                    </template>

                    <template v-if="item.title == 'CoExpression Categories'">
                        <div class="interactive-panel">
                            <div class="interactive-panel-header">
                                <!-- <h3>Interactive Summary Pie View</h3> -->
                                 <h3>CoExpression Summary Composition</h3>
                                <!-- <p>This interactive visualization provides a compact overview of the coexpression summary composition and complements the database description above.</p> -->
                                 <p>In total, CoExCell contains 211.9 million significant gene-gene correlations, of which 80.4% were derived from single-cell data and 19.6% from spatial data. Human datasets contributed 61.4% of all curated correlations, whereas mouse datasets contributed 38.6%. Lung, brain, bone marrow, kidney, and intestine accounted for the largest fractions of curated correlations.</p>
                            </div>
                            <iframe
                                class="interactive-frame"
                                src="/combined_summary_pie_interactive.html"
                                title="Interactive combined summary pie view"
                                loading="lazy"
                            ></iframe>
                        </div>
                    </template>

                    <template v-if="item.title == 'Resources'">
                        <div class="resources-list">
                            <div class="resource-category">
                                <h4>Functional Annotation Data</h4>
                                <ul class="resource-links">
                                    <li><a href="https://www.genome.jp/kegg/" target="_blank">KEGG</a> - Kyoto Encyclopedia of Genes and Genomes</li>
                                    <li><a href="https://geneontology.org/" target="_blank">GO</a> - Gene Ontology</li>
                                    <li><a href="https://scenic.aertslab.org/scenic_paper/tutorials/RcisTarget.html" target="_blank">RcisTarget</a> - Trancription Factor Target</li>
                                </ul>
                            </div>

                            <div class="resource-category">
                                <h4>Analysis Tool</h4>
                                <ul class="resource-links">
                                    <li><a href="https://changsubiostats.github.io/CS-CORE/" target="_blank">CSCORE</a> - cell-type-specific co-expression inference from single cell RNA-sequencing data.</li>
                                    <li><a href="https://smorabit.github.io/hdWGCNA/" target="_blank">HdWGCNA</a> - R package for performing weighted gene co-expression network analysis (WGCNA) in high dimensional transcriptomics data</li>
                                </ul>
                            </div>

                            <div class="resource-category">
                                <h4>Spatial RNA-seq data</h4>
                                <ul class="resource-links">
                                    <li><a href="https://cellxgene.cziscience.com/" target="_blank">CELLXGENE</a> - Single-cell data explorer</li>
                                    <li><a href="https://www.ncbi.nlm.nih.gov/geo/" target="_blank">Gene Expression Omnibus</a> - Functional genomics data repository</li>
                                    <li><a href="https://db.cngb.org/stomics/" target="_blank">STOmics DB</a> - Spatial Transcript Omics DataBase</li>
                                    <li><a href="http://www.spatialomics.org/SpatialDB/" target="_blank">SpatialDB</a> - Spatial Transcript Omics DataBase</li>
                                </ul>
                            </div>

                            <div class="resource-category">
                                <h4>Single Cell RNA-seq data</h4>
                                <ul class="resource-links">
                                    <li><a href="https://cellxgene.cziscience.com/" target="_blank">CELLXGENE</a> - Single-cell data explorer</li>
                                    <li><a href="https://www.ncbi.nlm.nih.gov/geo/" target="_blank">Gene Expression Omnibus</a> - Functional genomics data repository</li>
                                    <li><a href="https://tabula-muris-senis.sf.czbiohub.org/" target="_blank">Tabula Muris</a> - Mouse single cell resources</li>
                                </ul>
                            </div>
                        </div>
                    </template>

                    <template v-if="item.title == 'Contact'">
                        <div class="contact-grid">
                            <div class="contact-card">
                                <div class="contact-name">Frank Qingyun WANG</div>
                                <a href="mailto:frkwang@connect.hku.hk" class="contact-email">frkwang@connect.hku.hk</a>
                            </div>
                            <div class="contact-card">
                                <div class="contact-name">Chen Cao</div>
                                <a href="mailto:caochen@njmu.edu.cn" class="contact-email">caochen@njmu.edu.cn</a>
                            </div>
                        </div>
                    </template>
                </div>
            </section>
        </main>
    </div>
</template>

<script>
let contentList = [
    {
        title: "About CoExCell",
        content: [
            "CoExCell, a comprehensive resource that constructs cell type specific and spatially resolved gene coexpression networks by integrating single-cell and spatial transcriptomics. This resource directly addresses the central limitation of traditional bulk tissue coexpression analysis, which is intrinsically confounded by cellular heterogeneity, a statistical pitfall analogous to Simpson’s Paradox, where correlations measured at the tissue level often reflect fluctuating cell type proportions rather than genuine molecular interactions. The current version contains gene-gene correlation pairs from 92 single-cell and 93 spatial datasets across 34 human and mouse tissues and 445 unique cell types."
        ],
        images: ["/images/about/about_main.png"]
    },
    {
        title: "What is Simpson’s Paradox and Why this database matters?",
        content: [
            "Simpson’s Paradox is a well-known statistical phenomenon in which a clear trend, association, or correlation that exists within each subgroup of a dataset reverses, disappears, or changes direction when the subgroups are aggregated into a single combined dataset. It arises because of a hidden (confounding) variable—such as cell-type identity—that simultaneously affects both the variables being studied and the relative sizes or compositions of the subgroups.",
            "In the context of gene expression and co-expression analysis, this paradox is especially problematic. Bulk RNA-seq data pools transcripts from millions of cells of many different types. The resulting average expression profile can therefore produce misleading correlations that do not reflect the true biology occurring inside any individual cell type.",
            "Consider a tissue sample containing two distinct cell types and two genes, GeneX and GeneY. Cell Type A comprises the vast majority (900 cells) and shows strong positive co-expression between the two genes (correlation ≈ +0.75), with generally low expression of GeneX but high expression of GeneY. Cell Type B, a minority population (100 cells), also exhibits strong positive co-expression (correlation ≈ +0.80), but its cells express dramatically higher levels of GeneX and much lower levels of GeneY. When the expression data from both cell types are pooled—as occurs in conventional bulk RNA-seq—the overall correlation between GeneX and GeneY flips to strongly negative (e.g., ≈ −0.45 to −0.75). This reversal occurs because the extreme values from the smaller Cell Type B population disproportionately influence the aggregate statistics, even though they represent only ~10% of the cells. The hidden confounding factor，cell-type identity，thus creates the statistical illusion of an anti-correlation that does not exist within either individual cell type.",
            "Traditional bulk co-expression databases and many large-scale analyses suffer from exactly this form of Simpson’s Paradox. They report relationships that are artefacts of cell-type mixing rather than genuine regulatory biology. By providing single-cell resolved, cell-type-specific co-expression matrices, this database eliminates the confounding effect of cell-type heterogeneity. Researchers can now examine true positive or negative co-expression within each biologically meaningful cell population, avoid spurious conclusions drawn from aggregated data, discover cell-type-restricted regulatory networks that were previously invisible.",
            "In short, single-cell data resolves this classic instance of Simpson’s Paradox by allowing co-expression analysis to be performed separately within each biologically coherent population, revealing the true, cell-type-specific regulatory relationships."
        ],
    },
    /*{
        title: "CoExpression Categories",
        content: [
            "The interactive chart below provides a summary view of the major coexpression categories and serves as a visual companion to the database documentation."
        ],
    },*/
    {
        title: "Analysis Pipeline",
        content: [
            "To infer cell type specific gene coexpression from single cell RNA seq data, we applied the CSCORE framework, which models true underlying expression levels as latent variables to account for technical variation and measurement errors. It employs an Iteratively Reweighted Least Squares procedure to efficiently estimate gene specific mean expression and variance, as well as covariances between genes. A key feature is the use of carefully chosen weights, updated iteratively, to enhance statistical efficiency, with a regularization step applied to variance parameters to ensure stable estimation. The method includes a statistical test for gene independence, where a test statistic derived under the null hypothesis of independence follows a standard normal distribution, allowing for direct p value computation. For each cell type, we restricted the analysis to the top 3,000 highly variable genes detected in at least 10 cells to minimize the effect of collinearity from low variance features. Gene-gene correlation matrices generated by CSCORE were filtered to retain only statistically significant relationships (Bonferroni adjusted p < 0.05, Correlation Coefficient > 0.3). These significant correlations were then used as input for Weighted Gene Coexpression Network Analysis (WGCNA) [22]. Using the TOMsimilarityfunction, we computed topological overlap between genes, followed by dynamic tree cutting (cutreeDynamic) to identify coexpression modules, applying default parameters as established in the original method.",
            "For spatial transcriptomics data, we utilized the hdWGCNA pipeline to address the sparsity inherent in spatial profiles. To mitigate this sparsity, we employed the MetaspotsByGroups function to implement a metaspot aggregation algorithm. This method establishes a grid of evenly spaced principal spots that serve as anchor points, each combining with up to seven neighboring spots to form a consolidated metaspot, while limiting overlap to a maximum of two shared spots to preserve spatial independence. These metaspots improve the signal to noise ratio by generating an aggregated expression matrix. The aggregated expression values were first used to compute a Pearson correlation matrix. This matrix was then processed using Weighted Gene  coexpression Network Analysis (WGCNA), where topological overlap matrices (TOMsimilarity) and dynamic tree cutting (cutreeDynamic) were applied, consistent with the single cell RNA seq workflow, to define spatially informed  coexpression modules. This integrated approach ensures robust coexpression inference while preserving spatial context."
        ],
        images: ["/images/workflow.png"]
    },
    {
        title: "Cell Type/Spatially Resolved Modules",
        content: [
            "CoExCell defines and provides comprehensive annotations for cell type-specific and spatially resolved co-expression modules. Cell type-specific modules are derived by applying weighted gene co-expression network analysis (WGCNA) to correlation matrices computed from single-cell RNA-seq data within each individually annotated cell population. This approach ensures that each module represents a set of genes with tightly coordinated expression unique to that cell type, effectively deconvoluting tissue-level signals. Spatially resolved modules are constructed using a similar framework applied to spatially aggregated data from spatial transcriptomics, capturing gene co-expression patterns dictated by tissue morphology and local microenvironment. Each module, whether cell-type-specific or spatially defined, is extensively annotated with enriched Gene Ontology terms, KEGG pathways, and predicted transcription factor regulators. This structured annotation enables researchers to directly interpret modules as functional gene programs operating within distinct cellular compartments or anatomical niches, bridging high-resolution molecular networks with biological context."
        ]
    },
    {
        title: "Usage Guide",
        content: [
            "CoExCell provides an interactive web interface with tabs including Home, Single Cell, Spatial, Gene, Tutorial, and About. These tabs support bidirectional navigation between dataset and gene. Queries can start from a data or a gene.",
            "For both single cell and spatial data exploration, users can select a tissue of interest or an organism of interest, after which CoExCell lists all available datasets. For each dataset page, users can then choose to view the information in the “Gene” centric view or “Module” centric view. The Gene centric page presents comprehensive metadata, including donor characteristics, sample size, original publication, and data source, alongside interactive visualizations tailored to each data type. For single cell datasets, this includes a UMAP colored by cell type and a cell type composition pie chart. For spatial transcriptomics datasets, a tissue slice plot displays the spatial view of the data. A summary table enumerates all gene-gene paired correlation with detailed information is also provided. Users can filter by adjusted p value, correlation coefficients, cell type, gene1 and gene2 to query their interested gene pairs in interested cell types. Clicking the gene name will pop out a new page showing the expression level of the gene on the UMAP. In the bottom panel, users can select the gene1 and gene2 and cell type to visualize a correlation plot. In the “Module” centric page, it will show a table with all cell type specific coexpression modules. Users can click the module to show detailed information such as with a gene and functional annotation including GO, KEGG, potential regulatory TF of this cell type speific module.",
            "The Gene Exploration feature allows users to investigate gene co-expression patterns. Users first select the organism (Human/Mouse) to populate the available gene list. Clicking on a gene navigates to its dedicated page. This page includes a curated section listing all genes significantly co-expressed with the query gene. Users can filter these co-expressed genes by dataset, cell type, adjusted p-value, and correlation coefficient to examine context-specific relationships. A separate module page lists all co-expression modules to which the gene belongs. To ensure seamless navigation, we have implemented bidirectional links, enabling users to click directly from a gene of interest to explore its coexpression partners within a specific dataset, as well as to view the information of all modules the gene participates in."
        ]
    },
    {
        title: "Resources",
        content: []
    },
    {
        title: "Contact",
        content: [
            "For any inquiries about our database, please reach out to:"
        ]
    }
];

export default {
    name: "AboutPage",
    data() {
        return {
            contentList,
        };
    },
    methods: {
        generateId(title) {
            return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        }
    },
    mounted() {
        // Smooth scrolling for navigation links
        document.querySelectorAll('.nav-link').forEach(link => {
            link.addEventListener('click', function (e) {
                e.preventDefault();

                // Remove active class from all links
                document.querySelectorAll('.nav-link').forEach(l => l.classList.remove('active'));

                // Add active class to clicked link
                this.classList.add('active');

                // Smooth scroll to target section
                const targetId = this.getAttribute('href');
                const targetSection = document.querySelector(targetId);

                if (targetSection) {
                    targetSection.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        });

        // Update active navigation link based on scroll position
        window.addEventListener('scroll', function () {
            const sections = document.querySelectorAll('.section');
            const navLinks = document.querySelectorAll('.nav-link');

            let current = '';

            sections.forEach(section => {
                const sectionTop = section.offsetTop;
                // Add an offset to highlight the section a bit earlier when scrolling down
                if (pageYOffset >= sectionTop - 200) {
                    current = section.getAttribute('id');
                }
            });

            navLinks.forEach(link => {
                link.classList.remove('active');
                if (link.getAttribute('href') === `#${current}`) {
                    link.classList.add('active');
                }
            });
        });
    }
};
</script>

<style>
:root {
    --primary-color: #2d5a40;
    --secondary-color: #1a472a;
    --accent-color: #4a7c59;
    --text-primary: #2c3e50;
    --text-secondary: #5a6c7d;
    --background-light: #f8faf9;
    --background-white: #ffffff;
    --border-color: #e1e8ed;
    --success-color: #27ae60;
    --warning-color: #f39c12;
    --error-color: #e74c3c;
}
</style>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.container {
    margin: 0 auto;
    display: flex;
    min-height: 100vh;
    font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    line-height: 1.7;
    color: var(--text-primary);
    font-size: 17px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

/* Navigation Sidebar */
.nav {
    width: 280px;
    /* background: var(--background-white); */
    padding: 2rem 0;
    position: fixed;
    top: 60px; /* Adjust top value to match header height if any, otherwise stick to top */
    left: 0;
    height: calc(100vh - 60px);
    overflow-y: auto;
    border-right: 1px solid var(--border-color);
    box-shadow: 2px 0 20px rgba(0, 0, 0, 0.08);
    z-index: 100;
}

.nav-header {
    padding: 0 2rem 2rem 2rem;
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 1.5rem;
}

.nav-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
}

.nav-subtitle {
    font-size: 0.95rem;
    color: var(--text-secondary);
    font-weight: 500;
}

.nav-menu {
    list-style: none;
}

.nav-item {
    margin-bottom: 0.25rem;
}

.nav-link {
    display: block;
    padding: 0.875rem 2rem;
    color: var(--text-primary);
    text-decoration: none;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s ease;
    border-left: 3px solid transparent;
    position: relative;
}

.nav-link:hover {
    background: var(--background-light);
    color: var(--primary-color);
    border-left-color: var(--accent-color);
}

.nav-link.active {
    background: var(--background-light);
    color: var(--primary-color);
    border-left-color: var(--primary-color);
    font-weight: 700;
}

/* Main Content */
.main-content {
    flex: 1;
    margin-left: 280px; /* Same as nav width */
    padding: 3rem 4rem;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.05);
}

.page-header {
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid var(--border-color);
}

.page-title {
    font-family: 'Source Serif Pro', Georgia, serif;
    font-size: 2.9rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 1rem;
    line-height: 1.2;
}

.page-subtitle {
    font-size: 1.35rem;
    color: var(--text-secondary);
    font-weight: 500;
    line-height: 1.5;
}

.section {
    margin-bottom: 4rem;
    scroll-margin-top: 2rem;
}

.section-title {
    font-family: 'Source Serif Pro', Georgia, serif;
    font-size: 1.85rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid var(--border-color);
    position: relative;
}

.section-title::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 60px;
    height: 2px;
    background: var(--accent-color);
}

.section-content {
    font-size: 1.1rem;
    line-height: 1.8;
    color: var(--text-primary);
}

.section-content p {
    margin-bottom: 1.5rem;
    text-align: justify;
    font-weight: 500;
}

.section-content p:last-child {
    margin-bottom: 0;
}

.analysis-pipeline-image {
    display: block;
    width: 100%;
    max-width: 1808px;
    height: auto;
    margin: 2rem auto 0;
    border-radius: 10px;
}

.interactive-panel {
    margin: 2.25rem 0 0;
    padding: 1.5rem;
    border: 1px solid var(--border-color);
    border-radius: 16px;
    background: linear-gradient(180deg, #fbfdfb 0%, #f3f7f4 100%);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
}

.interactive-panel-header {
    margin-bottom: 1rem;
}

.interactive-panel-header h3 {
    margin-bottom: 0.5rem;
    color: var(--primary-color);
    font-size: 1.3rem;
    font-weight: 700;
}

.interactive-panel-header p {
    margin-bottom: 0;
    color: var(--text-secondary);
    text-align: left;
}

.interactive-frame {
    display: block;
    width: 100%;
    min-height: 480px;
    border: 1px solid var(--border-color);
    border-radius: 12px;
    background: #fff;
}

/* Highlighted Boxes */
.highlight-box {
    background: linear-gradient(135deg, var(--background-light) 0%, #f1f5f1 100%);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 2rem;
    margin: 2rem 0;
    position: relative;
}

.highlight-box::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
    border-radius: 12px 12px 0 0;
}

.highlight-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 1rem;
}

/* Resources Section */
.resources-list {
    margin: 2rem 0;
    max-width: 1000px;
}

.resource-category {
    margin-bottom: 2rem;
}

.resource-category h4 {
    color: var(--primary-color);
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid var(--border-color);
    position: relative;
}

.resource-category h4::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 40px;
    height: 2px;
    background: var(--accent-color);
}

.resource-links {
    list-style: none;
    padding-left: 0;
}

.resource-links li {
    margin-bottom: 0.8rem;
    padding-left: 1.5rem;
    position: relative;
    line-height: 1.6;
}

.resource-links li::before {
    content: '•';
    position: absolute;
    left: 0;
    color: var(--accent-color);
    font-weight: bold;
    font-size: 1.2rem;
}

.resource-links a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s ease;
    margin-right: 0.5rem;
}

.resource-links a:hover {
    color: var(--accent-color);
    text-decoration: underline;
}

/* Contact Section */
.contact-section {
    background: linear-gradient(135deg, var(--background-light) 0%, #f1f5f1 100%);
    border-radius: 12px;
    padding: 2.5rem;
    margin-top: 3rem;
}

.contact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    margin-top: 1.5rem;
    margin-bottom: 2rem;
}

.contact-card {
    /* background: var(--background-white); */
    border-radius: 8px;
    padding: 1.5rem;
    text-align: center;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    margin-bottom: 3rem;
    border: 1px solid ;
}

.contact-name {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
}

.contact-email {
    color: var(--accent-color);
    text-decoration: none;
    font-weight: 600;
}

.contact-email:hover {
    text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 1024px) {
    .container {
        flex-direction: column;
    }

    .nav {
        width: 100%;
        height: auto;
        position: static;
        padding: 1rem;
    }

    .main-content {
        padding: 2rem;
    }

    .page-title {
        font-size: 2.25rem;
    }
}

@media (max-width: 768px) {
        .main-content {
            padding: 1.5rem;
        }

        .page-title {
            font-size: 2rem;
        }

        .section-title {
            font-size: 1.5rem;
        }
    }
</style>
